'use strict';
/* ==========================================================================
   core-local/web.js — "Who Eats Whom? Follow the Arrows": the food-web model (DESIGN.md §4). API: core-local/WEB.md.
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/webcheck.js, tools/contentcheck.js,
   tools/bankcheck.js --register web=…) and in the page (window.Web). Registers itself with the question bank as `web`
   when core/bank.js is loaded first (lesson.json scripts order), so pool / labs.json formulas can say
   `web.survey('raccoon').before`.

   THE DATA: content/web.json (every organism, link, case and number cites its row of docs/topics/facts-food-web.md).
   Browser: read lazily from Game.content.web (or Web.load(json)); node: require('../content/web.json').
   Nothing about biology is written in this file: every fact comes from the data. The population model's numbers
   (Pop) are invented and marked assumed (facts row 7.9).

   An arrow = energy, from the eaten (`from`) to the eater (`to`) (facts row 2.1).
   ========================================================================== */
(function (root) {
  let DATA = null, IDX = null;

  /* ---------------- data ---------------- */
  function load(json) {
    if (!json || !Array.isArray(json.organisms)) throw new Error('web: load() needs content/web.json');
    DATA = json; IDX = null; memo.clear(); POP_CACHE.clear();
    return Web;
  }
  function data() {
    if (DATA) return DATA;
    const G = root.Game;
    if (G && G.content && G.content.web) return load(G.content.web), DATA;
    if (typeof require === 'function') {
      try { return load(require('../content/web.json')), DATA; } catch (e) { /* fall through */ }
    }
    throw new Error('web: content/web.json is not loaded (Game.content.web, or Web.load(json))');
  }
  function idx() {
    if (IDX) return IDX;
    const D = data();
    const org = new Map(D.organisms.map(o => [o.id, o]));
    const feed = D.links.map(l => Object.freeze({ kind: 'feed', ...l }));
    const deadTo = D.dead.to;
    const dead = D.organisms.filter(o => o.id !== deadTo).map(o => Object.freeze({
      kind: 'dead', from: o.id, to: deadTo, row: (D.dead.rows && D.dead.rows[o.id]) || D.dead.row, confidence: D.dead.confidence,
    }));
    const key = (a, b) => a + '>' + b;
    const feedMap = new Map(feed.map(l => [key(l.from, l.to), l]));
    const deadMap = new Map(dead.map(l => [key(l.from, l.to), l]));
    const eats = new Map(), eatenBy = new Map();
    for (const o of D.organisms) { eats.set(o.id, []); eatenBy.set(o.id, []); }
    for (const l of feed) { eats.get(l.to).push(l.from); eatenBy.get(l.from).push(l.to); }
    const cases = new Map(D.cases.map(c => [c.id, c]));
    IDX = { org, feed, dead, feedMap, deadMap, eats, eatenBy, cases, key };
    return IDX;
  }
  const org = id => { const o = idx().org.get(id); if (!o) throw new Error(`web: no organism "${id}"`); return o; };
  const has = id => idx().org.has(id);
  const ids = () => data().organisms.map(o => o.id);
  const organisms = () => data().organisms.slice();
  const natives = () => data().organisms.filter(o => o.origin !== 'invasive').map(o => o.id);
  const name = id => org(id).name;
  const short = id => org(id).short;
  const label = id => org(id).label || org(id).short;
  const cap = s => String(s).charAt(0).toUpperCase() + String(s).slice(1);
  const nm = id => short(id);                 // pool text: the short name ("apple snail", "small fish")
  const Nm = id => cap(short(id));            // an option: "Apple snail"
  const list = v => (Array.isArray(v) ? v : String(v).split(',')).map(s => String(s).trim()).filter(Boolean);

  /* ---------------- links ---------------- */
  function links(kind = 'feed') {
    const I = idx();
    return kind === 'feed' ? I.feed.slice() : kind === 'dead' ? I.dead.slice() : I.feed.concat(I.dead);
  }
  function link(a, b) { const I = idx(); return I.feedMap.get(I.key(a, b)) || I.deadMap.get(I.key(a, b)) || null; }
  const isLink = (a, b) => !!link(a, b);
  const isFeed = (a, b) => idx().feedMap.has(idx().key(a, b));
  // the facts file's killed links (web.json "killed") that a drawn arrow a → b matches, or null
  function killed(a, b) {
    for (const k of data().killed || []) {
      const m = k.match || {};
      if (m.from && m.from !== a) continue;
      if (m.to && m.to !== b) continue;
      if (m.toRole && !(has(b) && org(b).role === m.toRole)) continue;
      return k;
    }
    return null;
  }
  // why a drawn arrow a → b is rejected: null (a sourced arrow), 'backward' (b → a is sourced) or 'unsourced'
  function whyNot(a, b) {
    if (isLink(a, b)) return null;
    if (isLink(b, a)) return 'backward';
    return 'unsourced';
  }
  // the same with detail: {ok, reason, link, reverse, killed: {id, say, row} | null}
  function why(a, b) {
    const r = whyNot(a, b);
    return { ok: r === null, reason: r, link: link(a, b), reverse: r === 'backward' ? link(b, a) : null, killed: r === 'unsourced' ? killed(a, b) : null };
  }
  const eats = x => (org(x), idx().eats.get(x).slice());          // its foods (feeding links only)
  const eatenBy = x => (org(x), idx().eatenBy.get(x).slice());    // its eaters
  const role = x => org(x).role;
  const diet = x => org(x).diet;
  const primary = () => ids().filter(x => role(x) === 'consumer' && eats(x).some(f => role(f) === 'producer'));

  /* ---------------- chains (only "on this path": DESIGN.md §5.1) ---------------- */
  const LEVELS = ['producer', 'primary', 'secondary', 'tertiary'];
  function chain(c) {
    if (Array.isArray(c)) return c.slice();
    const C = data().chains || {};
    if (C[c]) return C[c].path.slice();
    if (typeof c === 'string' && c.includes(',')) return list(c);
    throw new Error(`web: no chain "${c}"`);
  }
  const chainOk = c => { const p = chain(c); return p.length >= 2 && role(p[0]) === 'producer' && p.every((x, i) => i === 0 || isFeed(p[i - 1], x)); };
  // the consumer level of x ON the given path: 'producer' | 'primary' | 'secondary' | 'tertiary' | null (not on it,
  // or beyond tertiary: the test stops at tertiary, facts row 1.7)
  function level(x, c) {
    const p = chain(c);
    if (!chainOk(p)) throw new Error(`web: "${p.join(' > ')}" is not a sourced path from a producer`);
    const i = p.indexOf(x);
    return i < 0 || i >= LEVELS.length ? null : LEVELS[i];
  }
  const levelWord = (x, c) => { const l = level(x, c); return l === 'producer' ? 'producer' : l ? l + ' consumer' : null; };
  // every simple directed path of feeding links from → to
  function chains(from, to, { max = 200 } = {}) {
    org(from); org(to);
    const out = [], path = [from], seen = new Set([from]);
    (function walk(x) {
      if (out.length >= max) return;
      if (x === to && path.length > 1) { out.push(path.slice()); return; }
      for (const y of eatenBy(x)) if (!seen.has(y)) { seen.add(y); path.push(y); walk(y); path.pop(); seen.delete(y); }
    })(from);
    if (from === to) return [];
    return out;
  }

  /* ---------------- relationships ---------------- */
  const SIGN = v => (v === '+' || v === 1 || v === '1' ? '+' : v === '-' || v === '−' || v === -1 || v === '-1' ? '-' : v === '0' || v === 0 ? '0' : null);
  // the name from the two signs (order does not matter); (+, -) → "parasitism or predation" (the case decides)
  function relation(ea, eb) {
    const a = SIGN(ea), b = SIGN(eb);
    if (!a || !b) return null;
    const s = [a, b].sort().join('');          // sorted: '+' < '-' < '0'
    return { '++': 'mutualism', '+0': 'commensalism', '+-': 'parasitism or predation', '--': 'competition' }[s] || null;
  }
  const cases = () => data().cases.slice();
  const kase = id => { const c = idx().cases.get(id); if (!c) throw new Error(`web: no case "${id}"`); return c; };
  const caseName = id => kase(id).key;                         // the keyed name ("mutualism", …, "not a parasite")
  const note = id => kase(id).note;
  const pair = id => kase(id).pair;
  const partnerA = id => kase(id).a, partnerB = id => kase(id).b;
  const signs = id => { const c = kase(id); return `${c.a.effect} and ${c.b.effect}`; };
  // the signs of a relationship NAME put on case c's two partners: signsFor('burrow', 'mutualism') → "gopher frog +, gopher tortoise +"
  // (the case's own key → its own signs, partner by partner)
  function signsFor(id, rel) {
    const c = kase(id);
    let a, b;
    if (rel == null || rel === c.key) { a = c.a.effect; b = c.b.effect; }
    else { const s = signsOf(rel); if (!s) throw new Error(`web: no signs for "${rel}"`); [a, b] = s.split(' and '); }
    return `${c.a.short} ${a}, ${c.b.short} ${b}`;
  }
  // the signs in words (question text: a "0" in "+ and 0" would read as a number)
  const signsWords = id => ({ '+ and +': 'a plus for both partners', '+ and 0': 'a plus for one partner and a zero for the other',
    '0 and +': 'a plus for one partner and a zero for the other', '+ and -': 'a plus for one partner and a minus for the other',
    '- and +': 'a plus for one partner and a minus for the other', '- and -': 'a minus for both partners' })[signs(id)];
  const signsOf = name => ({ mutualism: '+ and +', commensalism: '+ and 0', parasitism: '+ and -', predation: '+ and -', competition: '- and -' })[name] || null;
  // does relation(case signs) give the case's key? ("not a parasite": the signs must not be + and -)
  function relationMatches(id) {
    const c = kase(id), r = relation(c.a.effect, c.b.effect);
    if (c.key === 'not a parasite') return r !== null && r !== 'parasitism or predation';
    if (c.key === 'parasitism' || c.key === 'predation') return r === 'parasitism or predation';
    return r === c.key;
  }
  const NAMES = () => data().relations.names.slice();
  // the k-th relationship name that is NOT case c's key (pool distractors; k = 0, 1, … cycles)
  function otherName(id, k = 0) {
    const key = caseName(id), o = NAMES().filter(n => n !== key);
    return o[((k % o.length) + o.length) % o.length];
  }
  // a fixed, distinct distractor list for case c: for parasitism the first is predation, and vice versa
  function nameNot(id, k = 0) {
    const key = caseName(id);
    const pref = key === 'parasitism' ? ['predation', 'commensalism', 'mutualism'] : key === 'predation' ? ['parasitism', 'competition', 'mutualism']
      : key === 'mutualism' ? ['commensalism', 'parasitism', 'competition'] : key === 'commensalism' ? ['mutualism', 'parasitism', 'competition']
        : key === 'competition' ? ['predation', 'mutualism', 'commensalism'] : ['parasitism', 'mutualism', 'competition'];
    return pref[((k % pref.length) + pref.length) % pref.length];
  }
  const keyCases = name => cases().filter(c => c.verdict === 'KEY' && c.key === name).map(c => c.id);
  // "which pair shows …": the pair of a KEY case with a DIFFERENT key (k picks the name, s the case)
  function pairNot(id, k = 0, s = 0) {
    const n = nameNot(id, k), cs = keyCases(n);
    if (!cs.length) throw new Error(`web: no KEY case for "${n}"`);
    return pair(cs[((s % cs.length) + cs.length) % cs.length]);
  }

  /* ---------------- the road surveys (facts row 4.6) ---------------- */
  function survey(animal) {
    const S = data().survey, a = S.animals[animal];
    if (!a) throw new Error(`web: no survey animal "${animal}"`);
    const drop = a.dropPercent;
    const after = a.before != null && drop != null ? Math.round(a.before * (100 - drop) * 1e4) / 1e6 : (a.notSeen ? 0 : null);
    return {
      animal, name: a.name, organism: a.organism, before: a.before, dropPercent: drop, after, notSeen: !!a.notSeen,
      index: { before: 100, after: drop != null ? Math.round((100 - drop) * 10) / 10 : (a.notSeen ? 0 : null) },  // % of the earlier sightings
      unit: S.unit, row: S.row, computed: 'after = before × (100 − drop) ÷ 100 (computed from row 4.6)', missing: a.missing || null,
    };
  }
  const surveyAnimals = () => Object.keys(data().survey.animals);
  function fact(k) { const f = data().facts[k]; if (!f) throw new Error(`web: no fact "${k}"`); return f.value; }
  const factRow = k => { const f = data().facts[k]; if (!f) throw new Error(`web: no fact "${k}"`); return f.row; };

  /* ---------------- ripple: who a change touches (pure graph, no numbers) ---------------- */
  // change: 'remove' | 'add' (the arrows are the same either way: the reach along them in BOTH directions)
  // → {change, x, direct, touched: [{id, dist, paths, rel}], byDist: {1: [...], 2: [...]}, max}
  function ripple(change, x, { dead = false, among = null, maxPaths = 6 } = {}) {
    org(x);
    const inSet = among ? new Set(list(among)) : null;
    const ok = id => !inSet || inSet.has(id);
    const nb = id => {
      const out = new Set([...eats(id), ...eatenBy(id)]);
      if (dead) for (const l of links('dead')) { if (l.from === id) out.add(l.to); if (l.to === id) out.add(l.from); }
      return [...out].filter(ok);
    };
    const dist = new Map([[x, 0]]), paths = new Map([[x, [[x]]]]), q = [x];
    while (q.length) {
      const a = q.shift();
      for (const b of nb(a)) {
        const d = dist.get(a) + 1;
        if (!dist.has(b)) { dist.set(b, d); paths.set(b, []); q.push(b); }
        if (dist.get(b) === d) { const P = paths.get(b); for (const p of paths.get(a)) if (P.length < maxPaths) P.push([...p, b]); }
      }
    }
    const touched = [...dist.keys()].filter(id => id !== x).map(id => ({
      id, dist: dist.get(id), paths: paths.get(id),
      rel: eats(x).includes(id) ? 'food' : eatenBy(x).includes(id) ? 'eater' : 'further',
    })).sort((a, b) => a.dist - b.dist || ids().indexOf(a.id) - ids().indexOf(b.id));
    const byDist = {};
    for (const t of touched) (byDist[t.dist] = byDist[t.dist] || []).push(t.id);
    return { change, x, direct: byDist[1] ? byDist[1].slice() : [], touched, byDist, max: touched.reduce((m, t) => Math.max(m, t.dist), 0) };
  }

  // links between a and b along the arrows (either direction; feeding links only), or null if not connected
  function distance(a, b) { if (a === b) return 0; const t = ripple('remove', a).touched.find(x => x.id === b); return t ? t.dist : null; }

  /* ---------------- small webs for the pools (seeded) ---------------- */
  function hashSeed(s) {
    s = String(s); let h = 1779033703 ^ s.length;
    for (let i = 0; i < s.length; i++) { h = Math.imul(h ^ s.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
    h = Math.imul(h ^ (h >>> 16), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^ (h >>> 16)) >>> 0;
  }
  function rng(seed) {
    let a = hashSeed(seed);
    const r = () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    r.int = n => Math.floor(r() * n);
    r.pick = arr => arr[r.int(arr.length)];
    return r;
  }
  const memo = new Map();
  // the organisms a pool web may use: the feeding web (no decomposers; sawgrass has no live eater, row 3.41)
  const poolIds = () => ids().filter(x => role(x) !== 'decomposer' && (role(x) !== 'producer' || eatenBy(x).length));
  // a web object (organisms → links among them, roles, rows, a layout, the computed answers)
  function webOf(members, extra = {}) {
    const S = new Set(members), M = ids().filter(x => S.has(x));
    const L = links('feed').filter(l => S.has(l.from) && S.has(l.to)).map(l => ({ from: l.from, to: l.to, row: l.row, confidence: l.confidence, say: l.say || null }));
    const foods = x => L.filter(l => l.to === x).map(l => l.from), eaters = x => L.filter(l => l.from === x).map(l => l.to);
    const producers = M.filter(x => role(x) === 'producer'), consumers = M.filter(x => role(x) === 'consumer');
    const prim = consumers.filter(x => foods(x).some(f => role(f) === 'producer'));
    // rows: producers 0, a consumer = 1 + the lowest row among its foods here (shortest energy path)
    const row = new Map(producers.map(p => [p, 0]));
    for (let k = 0; k < M.length; k++) for (const x of consumers) { const r = foods(x).filter(f => row.has(f)).map(f => row.get(f) + 1); if (r.length) { const v = Math.min(...r); if (!row.has(x) || v < row.get(x)) row.set(x, v); } }
    const rows = Math.max(0, ...[...row.values()]);
    const layout = [];
    for (let r = 0; r <= rows; r++) {
      const at = M.filter(x => row.get(x) === r);
      at.forEach((x, i) => layout.push({ id: x, row: r, x: (i + 1) / (at.length + 1), y: rows ? 1 - r / rows : 0.5 }));
    }
    const connected = (() => {
      if (!M.length) return false;
      const seen = new Set([M[0]]), q = [M[0]];
      while (q.length) { const a = q.shift(); for (const b of [...foods(a), ...eaters(a)]) if (!seen.has(b)) { seen.add(b); q.push(b); } }
      return seen.size === M.length;
    })();
    return { organisms: M, links: L, producers, consumers, primary: prim, foods, eaters, row: x => (row.has(x) ? row.get(x) : null), layout, connected, ...extra };
  }
  // a seeded, connected small web of n organisms (5–8; default 5 + 0..3 from the seed) with a producer → A → B path,
  // every consumer with a food in it, and the pool answers (ask.*) computed from it
  // which: 'all' (default: the 15-organism web) or 'native' (no python: the web of stop 1, before the python arrives)
  function subweb(seed, n, which = 'all') {
    const k0 = `${seed}|${n}|${which}`;
    if (memo.has(k0)) return memo.get(k0);
    const r = rng('subweb:' + k0);
    const size = n == null ? 5 + r.int(4) : Math.max(5, Math.min(8, Math.round(+n)));
    const pool = new Set(poolIds().filter(x => which !== 'native' || org(x).origin !== 'invasive'));
    let set = null, two = null;
    for (let attempt = 0; attempt < 50 && !set; attempt++) {
      const p = r.pick(ids().filter(x => pool.has(x) && role(x) === 'producer'));
      const a = r.pick(eatenBy(p).filter(x => pool.has(x)));
      const bs = eatenBy(a).filter(x => pool.has(x) && x !== p);
      if (!bs.length) continue;
      const b = r.pick(bs);
      const S = new Set([p, a, b]);
      let guard = 0;
      while (S.size < size && guard++ < 100) {
        const cand = [...pool].filter(c => !S.has(c) && ([...eats(c), ...eatenBy(c)].some(y => S.has(y))) &&
          (role(c) === 'producer' || eats(c).some(f => S.has(f))));
        if (!cand.length) break;
        S.add(r.pick(cand));
      }
      if (S.size === size) { set = S; two = [p, a, b]; }
    }
    if (!set) throw new Error(`web.subweb: no web of ${size} for seed ${seed}`);
    const W = webOf([...set], { seed, n: size, twoStep: two });
    const [p, a, b] = two;
    const pick = arr => arr[r.int(arr.length)];
    // ---- the answers ----
    const nonPrim = W.consumers.filter(x => !W.primary.includes(x));
    const prim = pick(W.primary), higher = pick(nonPrim), prod = pick(W.producers);
    // who loses a food if x disappears: x = a consumer with an eater and a food here
    // (an organism that is both its food and its eater, the python / alligator pair, is neither the answer nor the distractor)
    const onlyEaters = x => W.eaters(x).filter(y => !W.foods(x).includes(y)), onlyFoods = x => W.foods(x).filter(y => !W.eaters(x).includes(y));
    const xs = W.consumers.filter(x => onlyEaters(x).length && onlyFoods(x).length);
    const lx = pick(xs), le = pick(onlyEaters(lx)), lf = pick(onlyFoods(lx));
    // the backward arrow: a producer → primary link drawn the wrong way (any reader can see an arrow INTO a producer
    // is wrong: producers make their own food; no organism knowledge needed, facts row 1.7)
    const bl = pick(W.links.filter(l => role(l.from) === 'producer'));
    // the right arrows offered beside it: never one of a two-way pair (python and alligator: both directions are real,
    // row 3.39, but a reader without organism knowledge can't tell)
    const twoWay = l => W.links.some(m => m.from === l.to && m.to === l.from);
    const others = W.links.filter(l => l !== bl && !twoWay(l));
    const o1 = pick(others), o2 = pick(others.filter(l => l !== o1));
    const arrowText = (f, t) => `${cap(nm(f))} to ${nm(t)}`;
    // competition: two eaters that share a food here (the test's own pattern, facts rows 1.10, 5.14)
    const shared = [];
    for (const x of W.consumers) for (const y of W.consumers) if (x < y && W.foods(x).some(f => W.foods(y).includes(f))) shared.push([x, y]);
    let comp = null;
    if (shared.length) {
      const [x, y] = pick(shared);
      const notX = W.organisms.filter(z => z !== x && z !== y && !W.foods(z).some(f => W.foods(x).includes(f)));
      const food = pick(W.foods(x).filter(f => W.foods(y).includes(f)));
      const wrong = notX.filter(z => z !== food);
      if (wrong.length) comp = { ok: true, x, xName: nm(x), answer: Nm(y), answerId: y, food: Nm(food), foodId: food, other: Nm(pick(wrong)) };
    }
    // one food (by the arrows here) vs many: the answer has exactly one food here AND no off-board foods in the data
    // (otherFood: the raccoon, bobcat … eat things the board doesn't show; never an answer or a distractor here)
    const one = W.consumers.filter(x => W.foods(x).length === 1 && !org(x).otherFood);
    const many = W.consumers.filter(x => W.foods(x).length >= 2);
    const oneFood = one.length && many.length ? { ok: true, answer: Nm(pick(one)), many: Nm(pick(many)), producer: Nm(prod) } : { ok: false };
    if (oneFood.ok) { oneFood.answerId = one.find(x => Nm(x) === oneFood.answer); oneFood.manyId = many.find(x => Nm(x) === oneFood.many); }
    // an invasive predator that eats only x: the populations it never eats that the change can reach
    const ix = pick(W.consumers.filter(x => W.foods(x).length));
    const rp = ripple('add', ix, { among: W.organisms });
    const reach = rp.touched.map(t => t.id);
    const invasive = { ok: reach.length > 0, x: ix, xName: nm(ix), answerId: pick(reach), reach };
    invasive.answer = Nm(invasive.answerId);
    invasive.dist = (rp.touched.find(t => t.id === invasive.answerId) || {}).dist + 1;   // links from the new predator
    W.ask = {
      primary: { answer: Nm(prim), answerId: prim, producer: Nm(prod), producerId: prod, higher: Nm(higher), higherId: higher },
      loses: { x: nm(lx), xId: lx, answer: Nm(le), answerId: le, food: Nm(lf), foodId: lf },
      backward: { producer: nm(bl.from), producerId: bl.from, eater: nm(bl.to), eaterId: bl.to,
        answer: arrowText(bl.to, bl.from), right1: arrowText(o1.from, o1.to), right2: arrowText(o2.from, o2.to) },
      competitor: comp || { ok: false },
      oneFood,
      invasive,
    };
    W.has = { competitor: !!comp, oneFood: !!oneFood.ok, invasive: invasive.ok };
    // the version to draw for "which arrow is backward": the same links, that one reversed
    W.withBackward = { organisms: W.organisms.slice(), layout: W.layout, links: W.links.map(l => (l === bl ? { ...l, from: l.to, to: l.from, reversed: true } : l)), reversed: { from: bl.to, to: bl.from } };
    memo.set(k0, Object.freeze(W));
    return W;
  }
  // a named small web (fixed pool items): web.sub('periphyton,apple_snail,snail_kite')
  function sub(members) { const k0 = 'sub|' + members; if (memo.has(k0)) return memo.get(k0); const W = Object.freeze(webOf(list(members))); memo.set(k0, W); return W; }

  /* ---------------- limiting factors (the bench; SC.7.L.17.3, facts row 1.3) ---------------- */
  // each factor = how many of the population that factor ALONE allows (a made-up bar); the lowest bar sets the limit.
  // predators / disease: the population left after their share (a bar like the others: high = little predation).
  const FACTORS = ['food', 'shelter', 'water', 'space', 'predators', 'disease', 'parasitism', 'nesting sites'];
  function limit(factors) {
    const bars = Object.entries(factors || {}).filter(([k, v]) => typeof v === 'number' && isFinite(v)).map(([k, v]) => ({ factor: k, value: v }));
    if (!bars.length) throw new Error('web.limit: no factors');
    const sorted = bars.slice().sort((a, b) => a.value - b.value || FACTORS.indexOf(a.factor) - FACTORS.indexOf(b.factor));
    const lo = sorted[0].value, tied = sorted.filter(b => b.value === lo).map(b => b.factor);
    return { factor: sorted[0].factor, limit: lo, tied, next: sorted[1] ? sorted[1].factor : null, nextLimit: sorted[1] ? sorted[1].value : null,
      order: sorted.map(b => b.factor), bars, label: 'A simplified model', assumed: true };
  }
  // positional, for pool formulas (core/bank.js formulas cannot write {…}): web.limitOf(food, shelter, water, space, predators, disease)
  function limitOf(food, shelter, water, space, predators, disease) {
    const f = { food, shelter, water, space };
    if (predators != null) f.predators = predators;
    if (disease != null) f.disease = disease;
    return limit(f).factor;
  }
  // the second-lowest and the highest bar (pool distractors: 'the next limit', 'the most to spare')
  const limitNext = (food, shelter, water, space) => limit({ food, shelter, water, space }).order[1];
  const limitMost = (food, shelter, water, space) => { const o = limit({ food, shelter, water, space }).order; return o[o.length - 1]; };
  const bench = id => { const B = (data().model.bench || {})[id]; if (!B) throw new Error(`web: no bench for "${id}"`); return B; };

  /* ---------------- Pop: the simplified population model (DESIGN.md §4; every number assumed, facts row 7.9) ---------------- */
  /* Discrete seasons t = 0..40 (t odd = dry season). For each population i (reference size n0, all made up):
       food   F = (Σ w_ij · N_j / n0_j + other_i) / F0_i   (F0 = the same in the native web at n0, where an arriving
              species counts 0; producers: F = 1, sunlight)
       caps   shelter, water, space (× n0); in the dry season a fish's water cap shrinks without an alligator hole:
              water × (dryLow + (1 − dryLow) · R), R = min(1, N_alligator / (refugeAt · n0_alligator))   (rows 4.15, 6.8)
       losses P = Σ q_pi · N_p / n0_p (predation), d (disease)
       target T = n0 · min(F, caps) / min(1, caps0) · (1 − P − d) / (1 − P0 − d)   (P0 = the losses in the native web)
       step   natives: N ← N + g · (T − N);  the python (arriving): N ← N + r · N · (1 − N / T)
     The native web at its reference sizes is a fixed point: the baseline is flat by construction. */
  const SEASONS = 40;
  const POP_CACHE = new Map();
  function popParams() {
    const M = data().model;
    if (!M || !M.species) throw new Error('web: content/web.json has no model.species');
    return M;
  }
  function popRun(scenario) {
    if (POP_CACHE.has(scenario)) return POP_CACHE.get(scenario);
    const M = popParams(), SP = M.species, SC = (M.scenarios || {})[scenario];
    if (!SC) throw new Error(`web.Pop: no scenario "${scenario}"`);
    const all = Object.keys(SP);
    const present = all.filter(id => !(SC.remove || []).includes(id) && (!SP[id].arrives || (SC.add || []).includes(id)));
    const removed = new Set(all.filter(id => !present.includes(id)));
    const N = {};
    for (const id of all) N[id] = removed.has(id) ? 0 : (SP[id].arrives ? SP[id].start : SP[id].n0);
    const native = id => !SP[id].arrives;
    const hunters = prey => all.filter(p => SP[p].hunts && SP[p].hunts[prey] != null);
    const P0 = id => Math.min(M.maxLoss, hunters(id).filter(native).reduce((s, p) => s + SP[p].hunts[id], 0));
    const capsOf = (id, Nn, t) => {
      const c = { ...(SP[id].caps || M.defaults.caps) };
      if (SP[id].refuge && t % 2 === 1) {
        const A = SP[M.refuge.from], R = Math.min(1, Nn[M.refuge.from] / (M.refuge.at * A.n0));
        c.water = c.water * (M.refuge.dryLow + (1 - M.refuge.dryLow) * R);
      }
      return c;
    };
    // food relative to the native web at its reference sizes (an arriving species counts 0 there)
    const F0 = id => { const s = SP[id]; if (!s.food) return 1; let F = s.food.other || 0; for (const [j, w] of Object.entries(s.food)) if (j !== 'other' && native(j)) F += w; return F; };
    const target = (id, Nn, t) => {
      const s = SP[id];
      let F = 1;
      if (s.food) { F = s.food.other || 0; for (const [j, w] of Object.entries(s.food)) if (j !== 'other') F += w * Nn[j] / SP[j].n0; F /= F0(id); }
      const c = capsOf(id, Nn, t), c0 = SP[id].caps || M.defaults.caps;
      const lim = Math.min(F, c.shelter, c.water, c.space) / Math.min(1, c0.shelter, c0.water, c0.space);
      const d = s.disease != null ? s.disease : M.defaults.disease;
      const P = Math.min(M.maxLoss, hunters(id).reduce((sum, p) => sum + SP[p].hunts[id] * Nn[p] / SP[p].n0, 0));
      return Math.max(0, s.n0 * lim * (1 - P - d) / (1 - P0(id) - d));
    };
    const series = Object.fromEntries(all.map(id => [id, [N[id]]]));
    for (let t = 0; t < SEASONS; t++) {
      const next = {};
      for (const id of all) {
        if (removed.has(id)) { next[id] = 0; continue; }
        const T = target(id, N, t + 1), s = SP[id];
        if (s.arrives) next[id] = T > 1e-9 ? N[id] + s.r * N[id] * (1 - N[id] / T) : N[id] * (1 - s.r);
        else next[id] = N[id] + (s.g != null ? s.g : M.defaults.g) * (T - N[id]);
        next[id] = Math.max(0, next[id]);
      }
      Object.assign(N, next);
      for (const id of all) series[id].push(N[id]);
    }
    const tags = SC.tags || {};
    const out = {
      scenario, seasons: SEASONS, organisms: present, removed: [...removed], series,
      rel: Object.fromEntries(all.map(id => [id, series[id].map(v => v / SP[id].n0)])),
      tags: Object.fromEntries(present.map(id => [id, tags[id] || SC.defaultTag || 'guess'])),
      rows: SC.rows || {}, prediction: !!SC.prediction, row: SC.row || null,
      label: popLabel(scenario), assumed: true, watch: SC.watch || null, note: SC.note || null,
    };
    out.guess = present.filter(id => out.tags[id] === 'guess');
    out.sourced = present.filter(id => /^sourced/.test(out.tags[id]));
    out.flat = present.filter(id => out.tags[id] === 'flat');
    POP_CACHE.set(scenario, Object.freeze(out));
    return out;
  }
  function popLabel(scenario) { const M = data().model; return scenario === 'noAlligator' ? M.labelSeasons : M.label; }
  // values at season t (fractional t interpolates) → {id: number}
  function popSample(scenario, t) {
    const R = popRun(scenario), k = Math.max(0, Math.min(SEASONS, +t || 0)), i = Math.floor(k), f = k - i;
    const o = {};
    for (const id of Object.keys(R.series)) { const s = R.series[id]; o[id] = i >= SEASONS ? s[SEASONS] : s[i] + (s[i + 1] - s[i]) * f; }
    return o;
  }
  const Pop = {
    SEASONS,
    get scenarios() { return Object.keys(popParams().scenarios); },
    run: popRun,
    sample: popSample,
    series: (scenario, id) => popRun(scenario).series[id].slice(),
    rel: (scenario, id) => popRun(scenario).rel[id].slice(),
    tags: scenario => ({ ...popRun(scenario).tags }),
    label: (scenario) => popLabel(scenario),
    realLabel: () => data().model.realLabel,
    dry: t => Math.floor(t) % 2 === 1,
    end: (scenario, id) => popRun(scenario).rel[id][SEASONS],                         // N / n0 at the last season
    mean: (scenario, id, from = SEASONS - 9) => { const r = popRun(scenario).rel[id].slice(from); return r.reduce((a, b) => a + b, 0) / r.length; },   // the mean of N / n0 over the last 10 seasons
    params: () => popParams(),
  };

  /* ---------------- export ---------------- */
  const Web = {
    load, data, has, ids, organisms, natives, org, name, short, label, nm, Nm, cap,
    links, link, isLink, isFeed, whyNot, why, killed, eats, eatenBy, role, diet, primary,
    LEVELS, chain, chainOk, level, levelWord, chains,
    relation, cases, case: kase, partnerA, partnerB, caseName, note, pair, signs, signsOf, signsFor, signsWords, relationMatches, otherName, nameNot, keyCases, pairNot,
    survey, surveyAnimals, fact, factRow,
    ripple, distance, subweb, sub, webOf, rng, hashSeed,
    FACTORS, limit, limitOf, limitNext, limitMost, bench,
    Pop,
  };
  root.Web = Web;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('web', Web);
  if (typeof module !== 'undefined' && module.exports) module.exports = Web;
})(typeof window !== 'undefined' ? window : globalThis);
